import React, { useState } from "react";
import useApi from "storybook-dashboard/utils/fetching";
import { Modal, useModal } from "storybook-dashboard/components/modal";

import { ProjectPermission } from "AppSrc/project/utils/permissions";
import { Spinner } from "traec-react/utils/entities";
import IndicatorRow from "./indicatorRow";
import { getProjectProps, BreadCrumb } from "AppSrc/project/utils";
import { CreateIndicatorForm } from "./form";
import { ErrorBoundary } from "storybook-dashboard/errors";
import { Hider } from "storybook-dashboard/utils";
import { useFullIds, useProjectPermissions } from "storybook-dashboard/utils/hooks";
import Im from "immutable";

function CreateIndicatorFormModal({
  projectId,
  trackerId,
  commitId,
  isRootRef,
  baseMetrics,
  refBaseMetrics,
  rootMetrics,
  triggerIndicators,
}) {
  const { hideModal } = useModal();

  // Using rootMetrics rather than baseMetrics so that the adding indicator
  // options are not pulled from other users/projects
  let useBaseMetrics = (isRootRef ? rootMetrics : refBaseMetrics) || baseMetrics;

  return (
    <Modal title="Add a new indicator">
      <ErrorBoundary>
        <CreateIndicatorForm
          {...{ projectId, trackerId, commitId }}
          baseMetrics={useBaseMetrics}
          toggleShowHandler={() => hideModal()}
          onSuccess={() => {
            triggerIndicators();
            hideModal();
          }}
        />
      </ErrorBoundary>
    </Modal>
  );
}

function AddIndicatorWrapper({ children }) {
  const { isViewer } = useProjectPermissions();
  if (isViewer) return null;
  return children;
}

function IndicatorListHeaders() {
  return (
    <div className="row">
      <div className="col-sm-4">
        <b>{"Name"}</b>
      </div>
      <div className="col-sm-2">
        <b>{"Sustainability Issue"}</b>
      </div>
      <div className="col-sm-4">
        <b>{"Function & Metrics"}</b>
      </div>
      <div className="col-sm-1">
        <b>Target</b> | <b>Warn</b>
      </div>
      <div className="col-sm-1">
        <b className="float-right">{"Admin"}</b>
      </div>
    </div>
  );
}

function IndicatorList(props) {
  let { commitIndicators, metricTargetMap, cref } = props;

  if (!commitIndicators) {
    return null;
  }

  return commitIndicators
    .toList()
    .sortBy((i) => i.getIn(["resultBaseMetric", "category"]) || i.get("category__name"))
    .map((indicator, i) => {
      let metricId = getResultBaseMetricId(indicator);
      let indicatorWithTarget = indicator.set("metricTarget", metricTargetMap.get(metricId));
      return (
        <ErrorBoundary key={i}>
          <IndicatorRow {...props} indicator={indicatorWithTarget} refId={cref.get("uid")} />
        </ErrorBoundary>
      );
    });
}

const getResultBaseMetricId = (indicator) => {
  return (
    indicator.getIn(["resultBaseMetric", "uid"]) ||
    indicator.getIn(["result_metric", "uid"]) ||
    indicator.get("result_metric__uid")
  );
};

export default function ProjectIndicators(props) {
  let { refBaseMetrics, rootMetrics } = props;

  const { setModal } = useModal();
  let { projectId, trackerId, commitId, isRootRef, company, project, cref, commit } = useFullIds();

  const { data: commitIndicators, trigger: triggerIndicators } = useApi(
    "/api/tracker/{trackerId}/commit/{commitId}/indicator/?light=true",
    {
      trackerId,
      commitId,
    }
  );

  const { data: commitTargets, trigger: triggerTargets } = useApi(
    "/api/tracker/{trackerId}/commit/{commitId}/target/",
    { trackerId, commitId }
  );
  const metricTargetMap = (commitTargets || Im.List())?.reduce(
    (map, target) => map.set(target.getIn(["metric", "uid"]), target),
    Im.Map()
  );

  // Get the base metrics at this level to use in the indicator create form
  const { data: commitNodes } = useApi("/api/tracker/{trackerId}/commit/{commitId}/node/?key_contains=metricscore", {
    trackerId,
    commitId,
  });
  const baseMetrics = (commitNodes || Im.List())
    ?.filter((i) => i.get("type") == "metricscore")
    ?.map((i) => i.getIn(["node", "metricscore", "metric"]))
    ?.reduce((acc, item) => acc.set(item.get("uid"), item), Im.Map());

  if (!project || !commitIndicators) {
    return <Spinner title="Loading..." explanation="Loading Indicator List. It might take a moment" />;
  }

  return (
    <ProjectPermission projectId={projectId} requiresAdmin={false}>
      <h3>Indicators</h3>
      <BreadCrumb company={company} project={project} cref={cref} isRootRef={isRootRef} />

      <p>
        Indicators are operations that are done to the metrics. The output from an indicator is a new metric that is
        derived from the operation.
      </p>
      <AddIndicatorWrapper>
        <button
          className="btn btn-sm btn-primary float-right"
          onClick={() =>
            setModal(
              <CreateIndicatorFormModal
                {...{
                  projectId,
                  trackerId,
                  commitId,
                  isRootRef,
                  baseMetrics,
                  refBaseMetrics,
                  rootMetrics,
                  triggerIndicators,
                }}
              />,
              true
            )
          }
        >
          Add a new indicator
        </button>
        <div style={{ clear: "both" }} />
      </AddIndicatorWrapper>
      <br />
      <br />
      <IndicatorListHeaders />
      <hr />
      <IndicatorList
        {...{
          commitIndicators,
          metricTargetMap,
          projectId,
          trackerId,
          commitId,
          baseMetrics,
          isRootRef,
          cref,
          commit,
          triggerIndicators,
          triggerTargets,
        }}
      />
    </ProjectPermission>
  );
}
